import { h } from 'preact';
import classNames from 'classnames';

import { useState, useEffect, useCallback } from 'preact/hooks';
import Image from 'utils/image';
import { IconMinusRound, IconPlusRound } from 'utils/icon';
import ProductOptionRow from '../ProductComponent/ProductOptionRow';

const GwpDropdown = ({
  data,
  sectionId,
  gwpLine,
  loading,
  setLoading,
  cart,
}) => {
  const { product } = data;
  const colorOptions = product.options_with_values.find(
    (opt) => opt.name?.toLowerCase() === 'color',
  );
  const sizeOptions = product.options_with_values.find(
    (opt) => opt.name?.toLowerCase() === 'size',
  );

  const [selectedVariant, setSelectedVariant] = useState(
    () => product.selected_or_first_available_variant,
  );
  const [sizeDrawer, setSizeDrawer] = useState(() => false);

  const [formOptions, setFormOptions] = useState(() => ({
    option_1: product.selected_or_first_available_variant?.option1,
    option_2: product.selected_or_first_available_variant?.option2,
    option_3: product.selected_or_first_available_variant?.option3,
    qty: 1,
  }));

  const attributes = [
    {
      key: '_inventory_quantity',
      value: selectedVariant.inventory_quantity,
    },
    { key: '_gwp', value: `${product.id}` },
  ];

  if (selectedVariant.lprop) {
    attributes.push({
      key: '_lprop',
      value: selectedVariant.lprop,
    });
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading) return;
    setLoading(true);
    if (gwpLine) {
      await cart.remove([gwpLine.key]);
    }
    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: {
          merchandiseId: `${selectedVariant.id}`,
          attributes,
        },
      },
    });
    document.dispatchEvent(addToCartEvent);
  };

  const getActiveVariant = useCallback(
    (event) => {
      const formData = serializeForm(event.target.closest('form'));

      formData.forEach((item) => {
        if (item.name.indexOf('option') !== -1) {
          setFormOptions((prevFormOptions) => ({
            ...prevFormOptions,
            [item.name]: item.value,
          }));
        }
      });
    },
    [formOptions],
  );

  useEffect(() => {
    const { variants } = product;
    const activeVariant = variants.find(
      (variant) =>
        variant.option1 === formOptions.option_1 &&
        variant.option2 === formOptions.option_2 &&
        variant.option3 === formOptions.option_3,
    );
    if (activeVariant) {
      setSelectedVariant(activeVariant);
    }
  }, [formOptions, product]);

  useEffect(() => {
    setSelectedVariant(product.selected_or_first_available_variant);
    setFormOptions(() => ({
      option_1: product.selected_or_first_available_variant?.option1,
      option_2: product.selected_or_first_available_variant?.option2,
      option_3: product.selected_or_first_available_variant?.option3,
      qty: 1,
    }));
    setSizeDrawer(false);
  }, [data]);

  const serializeForm = (form) => {
    const formData = new FormData(form);
    const serializedArray = [...formData.entries()].map(([name, value]) => ({
      name,
      value,
    }));
    return serializedArray;
  };

  const showDropdown =
    (!gwpLine || product.id !== gwpLine?.product_id) && !loading;

  return showDropdown ? (
    <div className="px-20 pb-16">
      <input
        type="checkbox"
        className="peer hidden"
        id="GWPDropdown"
      />
      <div className="relative block max-h-[34px] overflow-hidden rounded-[7px] border border-gray-900 px-8 transition-all before:absolute before:right-8 before:top-15 before:h-[1px] before:w-[9px] before:bg-gray-900 after:absolute after:right-12 after:top-11 after:h-[9px] after:w-[1px] after:bg-gray-900 peer-checked:max-h-[500px] peer-checked:after:hidden">
        <label
          htmlFor="GWPDropdown"
          className="relative block max-h-[34px]"
        >
          <h6 className="body-3-bold cursor-pointer py-8">
            {gwpLine
              ? data.gwp_dropdown_upgrade_title
              : data.gwp_dropdown_title}
          </h6>
        </label>
        {data.gwp_dropdown_description && (
          <p className="body-3 mb-8 text-gray-800">
            {data.gwp_dropdown_description}
          </p>
        )}
        <form
          action="/cart/add"
          method="post"
          encType="multipart/form-data"
          id="react-add-to-cart-form"
          onSubmit={handleSubmit}
          onChange={getActiveVariant}
          className="grid grid-cols-[96px_auto_auto] grid-rows-[auto_auto_auto] gap-x-16 border-t border-gray-600 pb-8 pt-8"
        >
          <Image
            {...selectedVariant?.featured_image}
            classes="col-start-1 col-end-2 row-span-2"
          />
          <div className="col-span-1 row-span-1">
            <h6 className="body-3-bold mb-8">{product.title}</h6>
            {colorOptions.values.length > 1 ? (
              <ProductOptionRow
                sectionId={sectionId}
                productId={product.id}
                sizeGuide={product.size_guide}
                hideSizeGuide={true}
                position={`option1`}
                option={colorOptions}
                productVariants={product.variants}
                formOptions={formOptions}
                setFormOptions={setFormOptions}
              />
            ) : (
              <p className="body-3 text-subdued">
                Color: {colorOptions?.values && colorOptions.values[0]}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end">
            <p className="body-3 uppercase text-gray-900">Free</p>
            {selectedVariant.compare_at_price ? (
              <span className="legal text-gray-800">
                {selectedVariant.compare_price_formatted} Comp. Value
              </span>
            ) : null}
          </div>
          <div className="col-span-2 row-span-1">
            {sizeOptions?.values?.length > 1 ? (
              <button
                className="body-2 flex w-full items-center justify-center gap-x-15 rounded-[20px] border border-gray-600 bg-inverse px-15 py-10"
                type="button"
                onClick={() => setSizeDrawer((prevState) => !prevState)}
              >
                Select Size
                {sizeDrawer ? (
                  <IconMinusRound className="w-15" />
                ) : (
                  <IconPlusRound className="h-15 w-15" />
                )}
              </button>
            ) : (
              <button
                type="submit"
                disabled={!selectedVariant.available || loading}
                className="button-primary mt-16 w-full border border-gray-900"
              >
                {gwpLine
                  ? data.gwp_dropdown_upgrade_cta
                  : data.gwp_dropdown_cta}
              </button>
            )}
          </div>
          <div
            className={classNames(
              'col-span-3 row-span-1 max-h-0 overflow-hidden transition-all',
              {
                'max-h-[450px]': sizeDrawer,
              },
            )}
          >
            <div className="px-1 pt-8 lg:pt-28">
              <ProductOptionRow
                fitText={product.fit_text}
                sizeGuide={product.size_guide}
                hideSizeGuide={false}
                sectionId={sectionId}
                productId={product.id}
                position={`option2`}
                option={sizeOptions}
                productVariants={product.variants}
                formOptions={formOptions}
                setFormOptions={setFormOptions}
                classes={
                  '!grid-cols-3 lg:!grid-cols-4 !gap-x-8 lg:!gap-x-10 !gap-10'
                }
              />
            </div>
            <button
              type="submit"
              disabled={!selectedVariant.available || loading}
              className="button-primary mt-16 w-full border border-gray-900"
            >
              {gwpLine ? data.gwp_dropdown_upgrade_cta : data.gwp_dropdown_cta}
            </button>
          </div>
        </form>
      </div>
    </div>
  ) : null;
};

export default GwpDropdown;
